import {
	ENVIRONMENT_LABEL_PREFIX,
	UNIT_ID_LABEL_PREFIX,
	WARNING_EMOJI
} from 'src/common/consts/platformConfigConsts';
import { ConfigurationLabel } from 'src/common/types/ConfigurationTypes';

import { ILabelConfig } from '../types/LabelsTypes';

const SPECIAL_LABEL_VALUES_WITHOUT_WARNING = new Set([
	'no option',
	'all options',
	'__ev_no_option__'
]);

/**
 * Checks if a value exists in a ConfigurationLabel's values array (case-insensitive)
 */
const isKnownValue = (value: string, labelConfig?: ConfigurationLabel): boolean => {
	if (!labelConfig?.values) {
		return false;
	}

	const valueLower = value.toLowerCase();
	return labelConfig.values.some((configValue) => {
		const configValueStr = typeof configValue === 'string' ? configValue : configValue.value;
		return configValueStr.toLowerCase() === valueLower;
	});
};

/**
 * Formats a label value with optional warning emoji if the value is not in the configured values
 */
export const formatLabelDisplayValue = (
	value: string,
	displayName: string,
	labelConfig?: ConfigurationLabel
): string => {
	if (!value || !displayName) {
		return value || '';
	}

	const normalizedValue = value.trim().toLowerCase();
	const isKnownValueResult = isKnownValue(value, labelConfig);
	const hasConfiguredValues = labelConfig?.values && labelConfig.values.length > 0;
	const shouldSkipWarning = SPECIAL_LABEL_VALUES_WITHOUT_WARNING.has(normalizedValue);

	if (!isKnownValueResult && hasConfiguredValues && !shouldSkipWarning) {
		return `${WARNING_EMOJI} ${displayName}: ${value}`;
	}

	return `${displayName}: ${value}`;
};

/**
 * Extracts the value part from a label string given a prefix
 * Handles case-insensitive matching and extracts everything after the colon
 */
export const extractLabelValue = (label: string, prefix: string): string => {
	if (!label) {
		return '';
	}

	const lowerLabel = label.toLowerCase();
	const lowerPrefix = prefix.toLowerCase();

	if (lowerLabel.startsWith(lowerPrefix)) {
		// Find the colon position in the original label (case-insensitive)
		const colonIndex = label.toLowerCase().indexOf(':');
		if (colonIndex >= 0) {
			return label.substring(colonIndex + 1).trim();
		}
		return label.substring(prefix.length).trim();
	}

	return label.trim();
};

/**
 * Checks if a label string starts with a given prefix (case-insensitive)
 */
export const isLabelString = (label: string, prefix: string): boolean => {
	if (!label) {
		return false;
	}
	return label.toLowerCase().startsWith(prefix.toLowerCase());
};

/**
 * Transforms labels array for display, adding warning emojis for unknown values
 * Supports both unitId and environment labels
 */
export const transformLabelsForDisplay = (
	labels: string[],
	isNewPlatformEnabled: boolean,
	unitIdConfig?: {
		displayName: string;
		labelConfig?: ConfigurationLabel;
		extractValue: (label: string) => string;
		isLabelString: (label: string) => boolean;
	},
	environmentConfig?: {
		displayName: string;
		labelConfig?: ConfigurationLabel;
		extractValue: (label: string) => string;
		isLabelString: (label: string) => boolean;
	}
): string[] => {
	if (!isNewPlatformEnabled || (!unitIdConfig?.displayName && !environmentConfig?.displayName)) {
		return labels;
	}

	return labels.map((label) => {
		if (unitIdConfig?.isLabelString(label) && unitIdConfig.displayName) {
			const value = unitIdConfig.extractValue(label);
			return formatLabelDisplayValue(value, unitIdConfig.displayName, unitIdConfig.labelConfig);
		}

		if (environmentConfig?.isLabelString(label) && environmentConfig.displayName) {
			const value = environmentConfig.extractValue(label);
			return formatLabelDisplayValue(
				value,
				environmentConfig.displayName,
				environmentConfig.labelConfig
			);
		}

		return label;
	});
};

export const transformCoreLabelsToCustomLabels = (
	coreLabels: ConfigurationLabel[]
): ILabelConfig[] => {
	return (coreLabels ?? []).map((label) => ({
		key: label.label,
		name: label.displayName ?? label.label,
		description: '',
		options: label.values.map((value) => {
			const valueStr = typeof value === 'string' ? value : value.value;
			return { key: valueStr, name: valueStr };
		})
	}));
};

export const applyMobileLabelDisplayNames = (
	labels: string[],
	unitIdDisplayName?: string,
	environmentDisplayName?: string
): string[] =>
	transformLabelsForDisplay(
		labels,
		true,
		unitIdDisplayName
			? {
					displayName: unitIdDisplayName,
					extractValue: (label: string) => extractLabelValue(label, UNIT_ID_LABEL_PREFIX),
					isLabelString: (label: string) => isLabelString(label, UNIT_ID_LABEL_PREFIX)
				}
			: undefined,
		environmentDisplayName
			? {
					displayName: environmentDisplayName,
					extractValue: (label: string) => extractLabelValue(label, ENVIRONMENT_LABEL_PREFIX),
					isLabelString: (label: string) => isLabelString(label, ENVIRONMENT_LABEL_PREFIX)
				}
			: undefined
	);
